/**
 * Timeline entry Zod schemas — shared contract between Terra API and
 * the frontend Accounts Timeline tab.
 *
 * Crosses a wire/UI boundary, so lives here in `src/shared/schemas/` and
 * is imported from the frontend via the `@shared/schemas/timeline-entry`
 * alias (see `web/app-home/tsconfig.json`).
 *
 * Schema is the source of truth. TS types below are `z.infer` only —
 * do NOT hand-write parallel interfaces (enforced by
 * `terra/no-parallel-schema-and-type`).
 *
 * Storage shape: `account_activity` (polymorphic — `source` enum +
 * `payload` JSONB). Source-specific fields live in `payload` and are
 * decoded at the API boundary by `Task/EventPayloadSchema` below; the
 * wire shape stays a discriminated union on `kind` so the frontend can
 * render kind-specific detail panels without re-discriminating on
 * source.
 *
 * Contract: see `plans/2026-04-24-001-accounts-timeline-tab-plan.md`
 * and bead bd-2la5.
 */
import { z } from 'zod';

/**
 * A single attendee on a Salesforce Event. Stored inside an event row's
 * `payload.attendees` and emitted on the wire as `attendees` on
 * `TimelineEventEntry`. Matches the upstream Middleware contract
 * (`plans/2026-04-24-002-middleman-resolve-account-activities-spec.md`).
 *
 * `type` is a free-form Salesforce EventRelation type ("Contact" | "User"
 * | "Lead"); kept as string to tolerate drift without dropping rows.
 */
export const TimelineAttendeeSchema = z.object({
  relationId: z.string(),
  name: z.string().nullable(),
  type: z.string(),
});

// ── Wire entries ────────────────────────────────────────────────────

/**
 * One member message of a collapsed email thread (U8, Bucket A/E plan).
 *
 * Gong logs every email of a conversation as its own Salesforce Task, so a
 * 6-email exchange renders as 6 near-identical timeline rows.
 * `buildAccountTimeline` collapses a chain into ONE task entry carrying the
 * members here — this is the expanded view's payload.
 *
 * Deliberately a LEAN projection of `TimelineTaskEntrySchema`, not a
 * recursive child of it: only the fields the expanded member list renders
 * (header line + description body). Recursing the full task entry would
 * need `z.lazy`, would let a member itself carry `threadMessages` (a shape
 * the producer never emits and the consumer would have to defend against),
 * and would put bucket/status/priority on the wire for rows that never
 * render them.
 *
 * `subject` is the RAW upstream subject — direction prefix and accumulated
 * `Re:`/`Fwd:` intact — because the expanded list shows the real per-message
 * subject line. The stripped form lives once on the collapsed entry's
 * `threadSubject`.
 */
export const TimelineThreadMessageSchema = z.object({
  /** SFDC Task id — same key space as `TimelineTaskEntry.id`, so the
   *  frontend can build a per-member "Open in Salesforce" link. */
  id: z.string(),
  /** RAW subject, prefixes intact (see the schema doc above). */
  subject: z.string().nullable(),
  description: z.string().nullable(),
  /** Derived SERVER-SIDE from the raw subject's `[Gong In]`/`[Gong Out]`
   *  prefix, so the client never re-implements the prefix parsing (and
   *  never re-trips the `[Gong Outbound]` near-miss). `null` when the
   *  subject carries neither prefix — a hand-logged email Task. */
  direction: z.enum(['inbound', 'outbound']).nullable(),
  /** ISO-8601, or null when the source row was undated. */
  effectiveAt: z.string().nullable(),
  activityDate: z.string().nullable(),
  ownerName: z.string().nullable(),
  contactName: z.string().nullable(),
  /** This MEMBER's own Gong fan-out count, if it was itself a collapsed
   *  set of per-attendee rows (Gong-id dedup runs BEFORE thread grouping).
   *  `.optional()` for the same reason as on the task entry — read as
   *  `?? 1`. */
  syncedRowCount: z.number().int().min(1).optional(),
});

/**
 * Timeline entry for a Salesforce Task.
 *
 * `activityDate` is an ISO date-only string (`YYYY-MM-DD`) carrying the
 * upstream task's due/activity date. `effectiveAt` is the canonical
 * display timestamp — server-computed from `activityDate` (UTC midnight)
 * and used for sort + bucket assignment.
 */
export const TimelineTaskEntrySchema = z.object({
  kind: z.literal('task'),
  id: z.string(),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  status: z.string().nullable(),
  priority: z.string().nullable(),
  isClosed: z.boolean(),
  activityDate: z.string().nullable(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  contactName: z.string().nullable(),
  /** Canonical timestamp the server sorted/bucketed on. ISO-8601, or null
   *  when the source row was undated. */
  effectiveAt: z.string().nullable(),
  /** Display bucket key the frontend groups by. One of:
   *    - `"upcoming"` — Upcoming & Overdue
   *    - `"YYYY-MM"`  — calendar month (UTC)
   *    - `"undated"`  — no `effectiveAt`
   */
  bucket: z.string(),
  /** `payload.gongActivityId`, echoed onto the wire entry so the frontend
   *  can key its own rendering decisions off it (though grouping itself
   *  happens server-side in `buildAccountTimeline` before entries reach
   *  the wire — see `syncedRowCount` below). `.optional()` rather than
   *  `.default()` (Decision 9: "`.optional()` OR defaulted") — a `.default`
   *  makes the key REQUIRED in the `z.infer`'d output type (zod fills it in,
   *  so it's always present), which would force every existing typed
   *  fixture/construction site that predates this field — e.g.
   *  `get-account-timeline.test.ts`'s `TIMELINE` fixture, `rowToEntry` in
   *  `account-timeline.ts` before the grouping unit lands — to be edited
   *  just to keep compiling. `.optional()` keeps the key genuinely optional
   *  so those sites are untouched; the grouping unit (U4) is what actually
   *  populates it for real entries. */
  gongActivityId: z.string().nullable().optional(),
  /** Number of raw `account_activity` rows this entry represents. `1` for
   *  an ungrouped row; `> 1` when `buildAccountTimeline` collapsed
   *  multiple rows sharing a non-null `gongActivityId` into one entry —
   *  drives the "×N synced rows" badge. See `gongActivityId` above for why
   *  this is `.optional()` rather than `.default(1)` — treat a missing
   *  value as `1` at the read site (`entry.syncedRowCount ?? 1`). */
  syncedRowCount: z.number().int().min(1).optional(),
  /** ── Email thread collapse (U8, Bucket A/E plan) ──
   *
   *  Present as a SET or not at all: `buildAccountTimeline` emits all three
   *  together on the one entry that represents a chain of ≥ 2 emails, and
   *  none of them on a singleton email, a non-email Task, or any Event.
   *  Absence is therefore the "this row is not a thread" signal — read
   *  sites gate on `threadCount` (or on `threadMessages` being non-empty),
   *  never on a sentinel value.
   *
   *  `.optional()` rather than `.default()` for the reason documented on
   *  `gongActivityId` above: a `.default()` makes the key REQUIRED in the
   *  `z.infer`'d type, which would force every pre-U8 fixture and every
   *  non-thread construction site in `rowToEntry` to be edited just to keep
   *  compiling — and would put a meaningless `threadCount: 1` on the wire
   *  for every ordinary row.
   *
   *  Number of email messages collapsed into this entry. Always ≥ 2 when
   *  present — a lone email is never given thread chrome. NOT the same
   *  count as `syncedRowCount`: that one counts duplicate SALESFORCE ROWS
   *  of a single activity (Gong's per-attendee fan-out), this one counts
   *  distinct MESSAGES of a conversation. Both can be present on one entry. */
  threadCount: z.number().int().min(2).optional(),
  /** The thread's normalized subject — direction prefix and every leading
   *  `Re:`/`Fwd:` stripped. Emitted so the collapsed row's title comes from
   *  the server (the normalization authority, same posture as `bucket`)
   *  instead of the client re-deriving it. */
  threadSubject: z.string().optional(),
  /** The collapsed messages, NEWEST first (Spencer's decision, 2026-08-28;
   *  upgraded same day to key on the row's real `created_at_sf` timestamp
   *  rather than the day-truncated `effectiveAt`, falling back to
   *  `effectiveAt` + feed order when `created_at_sf` is unavailable — see
   *  `groupEmailThreads` in `src/services/account-timeline.ts` for the exact
   *  ordering contract). Includes the entry's OWN message (the newest one,
   *  whose id/date/position this entry carries) as the FIRST element, so
   *  `threadMessages.length === threadCount` and the expanded list is
   *  complete without the consumer re-adding the parent. */
  threadMessages: z.array(TimelineThreadMessageSchema).optional(),
});

/**
 * Timeline entry for a Salesforce Event.
 *
 * `startDateTime` / `endDateTime` are ISO-8601 timestamps. `effectiveAt`
 * is the canonical display timestamp — server-computed from
 * `startDateTime`.
 */
export const TimelineEventEntrySchema = z.object({
  kind: z.literal('event'),
  id: z.string(),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  location: z.string().nullable(),
  isAllDay: z.boolean(),
  startDateTime: z.string().nullable(),
  endDateTime: z.string().nullable(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  contactName: z.string().nullable(),
  attendees: z.array(TimelineAttendeeSchema),
  /** Canonical timestamp the server sorted/bucketed on. ISO-8601, or null
   *  when the source row was undated. */
  effectiveAt: z.string().nullable(),
  /** Display bucket key — same convention as `TimelineTaskEntrySchema`. */
  bucket: z.string(),
  /** See `TimelineTaskEntrySchema.gongActivityId`. */
  gongActivityId: z.string().nullable().optional(),
  /** See `TimelineTaskEntrySchema.syncedRowCount`. */
  syncedRowCount: z.number().int().min(1).optional(),
});

/**
 * Timeline entry for a Salesforce `Events_and_Signals__c` record.
 *
 * Sourced from the `salesforce_event_and_signal` MIRROR table, not
 * `account_activity` — signals are interleaved into the same stream
 * server-side (GTMOPS-2162) rather than served from their own tab, so the
 * frontend gets one merged, one-sorted list. The read-path contract for
 * the standalone Signals surfaces stays `SignalItemSchema`
 * (`src/shared/schemas/sfdc-signals.ts`); this is the timeline-shaped
 * projection of the same row, carrying only what a timeline row renders.
 *
 * Deliberately narrower than `SignalItemSchema`:
 *   - no `accountId`/`accountName` — the timeline is already scoped to one
 *     account, so echoing it back would be dead weight;
 *   - no `contactId`/`contactName`/`source`/`createdDate`/
 *     `lastModifiedDate`/`isDeleted` — not rendered by a timeline row
 *     (`isDeleted` is a read FILTER, never displayed).
 *
 * `id` is the raw SF record id, so it feeds
 * `sfdcRecordUrl('Events_and_Signals__c', id)` directly.
 *
 * Titles: the UI renders `eventName ?? name` (same precedence as the
 * Signals surfaces) — `eventName` is the human display name
 * (`Event_Name__c`), `name` is the autonumber-ish SF standard `Name`.
 */
export const TimelineSignalEntrySchema = z.object({
  kind: z.literal('signal'),
  /** Raw `Events_and_Signals__c` record id. */
  id: z.string(),
  /** SF standard `Name` — autonumber-ish (a05…). Title fallback. */
  name: z.string().nullable(),
  /** SF `Event_Name__c` — the human display name. Preferred title. */
  eventName: z.string().nullable(),
  signalType: z.string().nullable(),
  signalSubtype: z.string().nullable(),
  description: z.string().nullable(),
  /** Canonical timestamp the server sorted/bucketed on — the mirror's
   *  `signalDate`. ISO-8601, or null when the signal is undated. */
  effectiveAt: z.string().nullable(),
  /** Display bucket key — same convention as `TimelineTaskEntrySchema`,
   *  minus the open-task pinning (signals have no open/closed state). */
  bucket: z.string(),
});

/**
 * Timeline entry for a `workspace_created` `account_activity` row — a Clay
 * `Workspace__c` record created for the account (Bucket B plan U9).
 *
 * `workspaceId` / `workspaceName` mirror `WorkspaceCreatedPayloadSchema`
 * verbatim. `contactName` is almost always null here — the response's
 * `creatorId` is `Clay_User__c`-typed, not a Contact id, so the
 * `SalesforceContact` name lookup essentially never resolves (see the
 * mapper doc in `src/services/account-activities/seams.ts`). That is the
 * expected, correct outcome, not a bug — the UI renders gracefully without
 * a creator name.
 */
export const TimelineWorkspaceCreatedEntrySchema = z.object({
  kind: z.literal('workspace_created'),
  /** The `account_activity.source_id` — the SF `Workspace__c` record id. */
  id: z.string(),
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  contactName: z.string().nullable(),
  /** Canonical timestamp the server sorted/bucketed on. ISO-8601, or null
   *  when the source row was undated. */
  effectiveAt: z.string().nullable(),
  /** Display bucket key — same convention as `TimelineTaskEntrySchema`,
   *  minus the open-task pinning (workspace rows have no open/closed state). */
  bucket: z.string(),
  // ── Bucket E fields threaded from `WorkspaceCreatedPayloadSchema` — see
  // that schema's field-by-field doc comments for the SF mapping of each.
  // All tolerant of absence (Decision 9): pre-U1 `account_activity` rows
  // carry none of these payload keys. ──
  workspaceUrl: z.string().nullable().optional(),
  activeStatus: z.string().nullable().optional(),
  activeTrial: z.boolean().optional(),
  subscriptionArr: z.number().nullable().optional(),
  numberOfClayUsers: z.number().nullable().optional(),
  productTier: z.string().nullable().optional(),
  lastTrialStartDate: z.string().nullable().optional(),
  lastTrialEndDate: z.string().nullable().optional(),
  hasChatGpt: z.boolean().optional(),
  chatGptConnectionDate: z.string().nullable().optional(),
  creatorName: z.string().nullable().optional(),
});

/**
 * Timeline entry for a `workspace_join` `account_activity` row — a
 * `Workspace_User_Relationship__c` join on one of the account's workspaces.
 *
 * `contactName` here resolves from a genuine Contact id (`join.contactId`),
 * so it fills in more often than the create entry's — still nullable for
 * free-email signups per the upstream contract.
 */
export const TimelineWorkspaceJoinEntrySchema = z.object({
  kind: z.literal('workspace_join'),
  /** The `account_activity.source_id` — the SF `Workspace_User_Relationship__c`
   *  record id. */
  id: z.string(),
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  /** Raw `Relationship_Type__c` picklist value. Free-form on purpose. */
  relationshipType: z.string().nullable(),
  contactName: z.string().nullable(),
  effectiveAt: z.string().nullable(),
  bucket: z.string(),
});

/**
 * Discriminated union of all timeline entry kinds. The UI bucketizer
 * switches on `kind` to render the right row.
 */
export const TimelineEntrySchema = z.discriminatedUnion('kind', [
  TimelineTaskEntrySchema,
  TimelineEventEntrySchema,
  TimelineSignalEntrySchema,
  TimelineWorkspaceCreatedEntrySchema,
  TimelineWorkspaceJoinEntrySchema,
]);

/**
 * Response shape for `GET /api/accounts/:accountId/timeline`.
 *
 * - `hasAccess: false` — either the account is unknown OR it is not in the
 *   caller's owned set. Always returned with HTTP 200
 *   (graceful degradation, R7). Never 403.
 * - `lastSyncedAt` — max(syncedAt) across the returned rows; `null`
 *   when the account has no activity rows yet.
 * - `truncated` — `true` when the row cap was hit (we returned the
 *   freshest N; the UI surfaces a "showing most recent N" line). Honest
 *   replacement for the previous "X items" count which silently lied.
 */
export const TimelineResponseSchema = z.object({
  entries: z.array(TimelineEntrySchema),
  lastSyncedAt: z.string().nullable(),
  hasAccess: z.boolean(),
  truncated: z.boolean(),
});

// ── DB JSON payload schemas (account_activity.payload) ───────────────

/**
 * Source-specific fields stored in `account_activity.payload` for
 * `source = 'salesforce_task'` rows. Flat columns (subject, owner, …)
 * live on the row itself; the payload only carries what's
 * task-specific.
 *
 * Read with `parseDbJson` (never `.parse` directly) — a single drifted
 * row must not 500 the endpoint.
 */
export const TaskPayloadSchema = z.object({
  isClosed: z.boolean(),
  status: z.string().nullable(),
  priority: z.string().nullable(),
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  /** ISO date-only (`YYYY-MM-DD`); the row's `effective_at` is derived
   *  from this. Kept here too so the wire entry can echo the source's
   *  own date semantics in the detail panel. */
  activityDate: z.string().nullable(),
  /** `Gong__Gong_Activity_Id__c` (terra-middleware PR #236) — the id
   *  `buildAccountTimeline` groups same-meeting rows by (Decision 9 in the
   *  Bucket A/E plan). `.nullable().optional()`, NOT a bare `.nullable()`,
   *  so `account_activity` rows persisted before this field existed still
   *  parse via `parseDbJson` — same deployment-skew idiom as
   *  `SfTaskSchema.gongActivityId`. */
  gongActivityId: z.string().nullable().optional(),
  /** Wave 1 receipt slice (terra-middleware
   *  feat/account-activity-participants-truncation). All `.optional()` so rows
   *  persisted before the field existed still parse via `parseDbJson`.
   *  `gongParticipantsEmails` is the raw SFDC TextArea — parsed defensively by
   *  the Delphi assembler, never here. `descriptionTruncated` /
   *  `descriptionOriginalLength` back an honest truncation receipt (a stored
   *  `null` means "unknown", i.e. persisted by a Middleman that predated the
   *  field — distinct from a known `false`). */
  gongParticipantsEmails: z.string().nullable().optional(),
  descriptionTruncated: z.boolean().nullable().optional(),
  descriptionOriginalLength: z.number().nullable().optional(),
});

/**
 * Source-specific fields stored in `account_activity.payload` for
 * `source = 'salesforce_event'` rows.
 */
export const EventPayloadSchema = z.object({
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  location: z.string().nullable(),
  isAllDay: z.boolean(),
  /** ISO-8601 timestamp; `effective_at` is derived from this. */
  startDateTime: z.string().nullable(),
  endDateTime: z.string().nullable(),
  attendees: z.array(TimelineAttendeeSchema),
  /** `Gong__Gong_Activity_Id__c` (terra-middleware PR #236) — same
   *  grouping key and deployment-skew rationale as `TaskPayloadSchema.
   *  gongActivityId`. */
  gongActivityId: z.string().nullable().optional(),
  /** Wave 1 receipt slice — same semantics as `TaskPayloadSchema`. */
  gongParticipantsEmails: z.string().nullable().optional(),
  descriptionTruncated: z.boolean().nullable().optional(),
  descriptionOriginalLength: z.number().nullable().optional(),
});

/**
 * Source-specific fields stored in `account_activity.payload` for
 * `source = 'workspace_created'` rows — a Clay `Workspace__c` record created
 * for the account (Bucket B plan U9, Middleman
 * `/salesforce/resolve-account-workspace-activity`).
 *
 * `workspaceId` is the CLAY APPLICATION workspace id (`Workspace_ID__c`) — the
 * `https://app.clay.com/workspaces/{workspaceId}` link target. It is NOT an SF
 * id and is NOT parsed out of the name (the "Company (id)" format is an
 * observed convention, not a contract). The row's `source_id` carries the SF
 * record id instead. Nullable because the upstream field is (~100% filled in
 * prod, but the contract permits null) — a row with no `workspaceId` renders
 * without a workspace link rather than with a broken one.
 *
 * `workspaceName` is the raw `Workspace__c.Name`, passed through unchanged.
 *
 * Read with `parseDbJson` (never `.parse` directly).
 */
export const WorkspaceCreatedPayloadSchema = z.object({
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  // ── Bucket E — Workspaces accordion fields (all `.nullable().optional()`,
  // tolerant of absence per Decision 9: `account_activity` rows persisted
  // before this unit landed carry none of these keys and must still
  // parse). ──
  /** Already on the wire pre-PR-#255, previously undeclared/unconsumed —
   *  `SfWorkspaceCreateSchema.workspaceUrl`. */
  workspaceUrl: z.string().nullable().optional(),
  /** `SfWorkspaceCreateSchema.activeStatus`. */
  activeStatus: z.string().nullable().optional(),
  /** `SfWorkspaceCreateSchema.activeTrial`. `.optional()` only (no
   *  `.nullable()`) — mirrors the upstream boolean's own shape. */
  activeTrial: z.boolean().optional(),
  /** `SfWorkspaceCreateSchema.subscriptionArr`. Historical note: an earlier
   *  version of the Paid/Trial classification (Decision 5) used `subscriptionArr
   *  > 0` as the paid signal; that was superseded 2026-08-28 after verifying
   *  against live SFDC FlexiPage metadata that the real rule is
   *  `productTier` non-null, with no ARR involvement at all (see
   *  `src/api/account-workspaces.ts`'s `classify()`). This field is still
   *  carried on the wire and used as the paid-table sort key, just no longer
   *  for classification. */
  subscriptionArr: z.number().nullable().optional(),
  /** `SfWorkspaceCreateSchema.numberOfClayUsers`. */
  numberOfClayUsers: z.number().nullable().optional(),
  // ── terra-middleware PR #255 fields — see `SfWorkspaceCreateSchema` in
  // `src/services/salesforce/reads/schemas.ts` for the SF field mapping of each. ──
  productTier: z.string().nullable().optional(),
  lastTrialStartDate: z.string().nullable().optional(),
  lastTrialEndDate: z.string().nullable().optional(),
  /** SF checkbox — `.optional()` only, deployment-skew "field absent". */
  hasChatGpt: z.boolean().optional(),
  chatGptConnectionDate: z.string().nullable().optional(),
  creatorName: z.string().nullable().optional(),
});

/**
 * Source-specific fields stored in `account_activity.payload` for
 * `source = 'workspace_join'` rows — a `Workspace_User_Relationship__c` join on
 * one of the account's workspaces.
 *
 * Same `workspaceId` semantics as `WorkspaceCreatedPayloadSchema`.
 * `relationshipType` is the raw `Relationship_Type__c` picklist ("Joined
 * Workspace", "Workspace Created", …) — free-form on purpose, so a new upstream
 * value never drops the row.
 *
 * The joining contact lives on the ROW (`contact_id` / `contact_name`), not in
 * the payload, so it uses the same columns as tasks and events.
 */
export const WorkspaceJoinPayloadSchema = z.object({
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  relationshipType: z.string().nullable(),
});

export type TimelineAttendee = z.infer<typeof TimelineAttendeeSchema>;
export type TimelineThreadMessage = z.infer<typeof TimelineThreadMessageSchema>;
export type TimelineTaskEntry = z.infer<typeof TimelineTaskEntrySchema>;
export type TimelineEventEntry = z.infer<typeof TimelineEventEntrySchema>;
export type TimelineSignalEntry = z.infer<typeof TimelineSignalEntrySchema>;
export type TimelineWorkspaceCreatedEntry = z.infer<typeof TimelineWorkspaceCreatedEntrySchema>;
export type TimelineWorkspaceJoinEntry = z.infer<typeof TimelineWorkspaceJoinEntrySchema>;
export type TimelineEntry = z.infer<typeof TimelineEntrySchema>;
export type TimelineResponse = z.infer<typeof TimelineResponseSchema>;
export type TaskPayload = z.infer<typeof TaskPayloadSchema>;
export type EventPayload = z.infer<typeof EventPayloadSchema>;
export type WorkspaceCreatedPayload = z.infer<typeof WorkspaceCreatedPayloadSchema>;
export type WorkspaceJoinPayload = z.infer<typeof WorkspaceJoinPayloadSchema>;
